import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useTenantBranding } from '../contexts/TenantBrandingContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Heart, Eye, EyeOff, Loader2, Sparkles, Shield, Zap, Users, Sun, Moon, FileText, Calendar, ArrowLeft, Mail, CheckCircle, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const { login } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { branding } = useTenantBranding();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}!`);
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid credentials';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email address');
      return;
    }
    
    setForgotLoading(true);
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success('Reset link sent! Check your email.');
    } catch (error) {
      // Always show success to prevent email enumeration
      setForgotSent(true);
      toast.success('If your email exists, you will receive a reset link.');
    } finally {
      setForgotLoading(false);
    }
  };

  const resetForgotState = () => {
    setShowForgotPassword(false);
    setForgotEmail('');
    setForgotSent(false);
  };

  const features = [
    { 
      icon: Zap, 
      title: 'Real-time Project Tracking',
      description: 'Monitor project progress, milestones, and deadlines with live updates and visual dashboards'
    },
    { 
      icon: Users, 
      title: 'Team Collaboration',
      description: 'Seamlessly communicate with your team and clients through integrated chat and task management'
    },
    { 
      icon: Shield, 
      title: 'Secure Client Portal',
      description: 'Give clients their own branded portal to view progress, approve work, and manage invoices'
    },
    { 
      icon: FileText, 
      title: 'Invoice & Billing',
      description: 'Create professional invoices, track payments, and manage recurring billing all in one place'
    },
    { 
      icon: Calendar, 
      title: 'Meeting Scheduler',
      description: 'Schedule meetings, send automated reminders, and keep everyone on the same page'
    },
  ];

  return (
    <div className="login-page" data-testid="login-page">
      {/* Theme toggle button - top right */}
      <button
        onClick={toggleTheme}
        className="fixed top-4 right-4 z-50 p-3 rounded-xl bg-white/10 backdrop-blur-lg border border-white/20 hover:bg-white/20 transition-all duration-300 shadow-lg"
        data-testid="login-theme-toggle"
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {isDark ? (
          <Sun size={20} className="text-amber-400" />
        ) : (
          <Moon size={20} className="text-slate-700" />
        )}
      </button>

      {/* Left side - Hero section */}
      <div className="login-hero">
        <div className="relative z-10 px-8 lg:px-12 max-w-2xl">
          {/* Logo */}
          {branding ? (
            /* White-label: show tenant branding */
            <div className="flex items-center gap-4 mb-6">
              {branding.logo ? (
                <img
                  src={`data:${branding.logo_content_type || 'image/png'};base64,${branding.logo}`}
                  alt={branding.company_name || branding.name}
                  className="h-16 w-auto max-w-[220px] object-contain rounded-xl"
                />
              ) : (
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-white" />
                </div>
              )}
              <h1 className="text-3xl lg:text-4xl font-bold text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                {branding.company_name || branding.name}
              </h1>
            </div>
          ) : (
            /* Default: My Client PM branding */
            <div className="flex items-center gap-4 mb-6">
              <div className="relative">
                <Heart className="w-12 h-12 text-white fill-white heart-logo" />
                <Sparkles className="w-4 h-4 text-blue-400 absolute -top-1 -right-1 animate-pulse" />
              </div>
              <h1 className="text-3xl lg:text-4xl font-bold text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                My Client PM
              </h1>
            </div>
          )}
          
          {/* Tagline */}
          <p className="text-lg lg:text-xl text-slate-300 mb-3 leading-relaxed">
            {branding
              ? `Welcome to ${branding.company_name || branding.name}'s client portal.`
              : 'The all-in-one project management platform for web design agencies and digital professionals.'}
          </p>
          <p className="text-sm text-slate-400 mb-8 leading-relaxed">
            {branding
              ? 'Sign in below to view your projects, invoices, and updates.'
              : 'Manage clients, projects, invoices, and team collaboration — all from a single, beautifully designed dashboard.'}
          </p>
          
          {/* Feature list */}
          <div className="space-y-4">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="flex items-start gap-4 animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center flex-shrink-0 mt-0.5">
                  <feature.icon className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm mb-1">{feature.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          {/* Stats */}
          <div className="flex items-center gap-8 mt-10 pt-8 border-t border-white/10">
            <div>
              <p className="text-2xl font-bold text-white">500+</p>
              <p className="text-xs text-slate-400">Projects Managed</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-white">50+</p>
              <p className="text-xs text-slate-400">Happy Agencies</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-white">99.9%</p>
              <p className="text-xs text-slate-400">Uptime</p>
            </div>
          </div>
          
          {/* Decorative gradient orb */}
          <div className="absolute -bottom-20 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-gradient-to-t from-blue-500/20 to-transparent rounded-full blur-3xl pointer-events-none" />
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="login-form-container">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            {branding ? (
              branding.logo ? (
                <img
                  src={`data:${branding.logo_content_type || 'image/png'};base64,${branding.logo}`}
                  alt={branding.company_name || branding.name}
                  className="h-10 w-auto max-w-[180px] object-contain"
                />
              ) : (
                <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                  {branding.company_name || branding.name}
                </span>
              )
            ) : (
              <>
                <Heart className="w-10 h-10 text-slate-900 fill-slate-900" />
                <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                  My Client PM
                </span>
              </>
            )}
          </div>

          {/* Form Card */}
          <div className="form-card animate-fade-in">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-700 mb-4 shadow-lg shadow-slate-900/20">
                {branding?.logo ? (
                  <img
                    src={`data:${branding.logo_content_type || 'image/png'};base64,${branding.logo}`}
                    alt={branding.company_name || branding.name}
                    className="w-10 h-10 object-contain rounded-lg"
                  />
                ) : (
                  <Shield className="w-8 h-8 text-white" />
                )}
              </div>
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                Welcome Back
              </h2>
              <p className="text-slate-500 mt-2">
                {branding
                  ? `Sign in to access ${branding.company_name || branding.name}`
                  : 'Sign in to access your dashboard'}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="form-label">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="form-input"
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="form-label">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="form-input pr-12"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    data-testid="toggle-password-btn"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl shadow-lg shadow-slate-900/20 transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5"
                data-testid="login-submit-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            {/* Forgot Password Link */}
            <div className="text-center mt-4">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </button>
            </div>

            {/* Footer text */}
            <p className="text-center text-sm text-slate-500 mt-6">
              Contact your administrator if you don't have an account
            </p>
          </div>

          {/* Copyright */}
          <p className="text-center text-xs text-slate-400 mt-8">
            © {new Date().getFullYear()} My Client PM. All rights reserved.
          </p>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-fade-in">
            <div className="p-6">
              {!forgotSent ? (
                <>
                  {/* Header */}
                  <div className="flex items-center gap-3 mb-6">
                    <button
                      onClick={resetForgotState}
                      className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                      data-testid="back-to-login-btn"
                    >
                      <ArrowLeft size={20} className="text-slate-600" />
                    </button>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900">Reset Password</h3>
                      <p className="text-sm text-slate-500">Enter your email to receive a reset link</p>
                    </div>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="forgot-email" className="form-label">
                        Email Address
                      </Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <Input
                          id="forgot-email"
                          type="email"
                          placeholder="you@example.com"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          required
                          className="form-input pl-10"
                          data-testid="forgot-email-input"
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={forgotLoading}
                      className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl"
                      data-testid="send-reset-link-btn"
                    >
                      {forgotLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        'Send Reset Link'
                      )}
                    </Button>
                  </form>

                  <button
                    onClick={resetForgotState}
                    className="w-full text-center text-sm text-slate-500 hover:text-slate-700 mt-4 transition-colors"
                  >
                    Back to Login
                  </button>
                </>
              ) : (
                <>
                  {/* Success State */}
                  <div className="text-center py-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Check Your Email</h3>
                    <p className="text-slate-500 mb-6">
                      We've sent a password reset link to<br />
                      <span className="font-medium text-slate-700">{forgotEmail}</span>
                    </p>
                    <p className="text-sm text-slate-400 mb-6">
                      The link will expire in 1 hour. If you don't see the email, check your spam folder.
                    </p>
                    <Button
                      onClick={resetForgotState}
                      className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl"
                      data-testid="back-to-login-after-reset-btn"
                    >
                      Back to Login
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
